import { useEffect, useRef, useState } from "react";
import { deckSourceContent, setDeckSourceContent, type SurfDeck } from "@vida/surf-core";

type Props = {
  deck: SurfDeck;
  onDeckChange: (deck: SurfDeck) => void;
};

export function ContentPanel({ deck, onDeckChange }: Props) {
  const hasPersistedSource = deck.sourceContent != null && deck.sourceContent !== "";
  const legacyExcerpt =
    !deck.sourceContent && deck.deckContext?.sourceExcerpts?.length
      ? deck.deckContext.sourceExcerpts.join("\n\n")
      : "";
  const [value, setValue] = useState(deck.sourceContent ?? "");
  const [excerptOpen, setExcerptOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValue(deck.sourceContent ?? "");
  }, [deck.id, deck.sourceContent]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const commit = (text: string) => {
    const next = setDeckSourceContent(deck, text);
    if (next !== deck) onDeckChange(next);
  };

  const handleChange = (text: string) => {
    setValue(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commit(text), 500);
  };

  const handleBlur = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commit(value);
  };

  return (
    <div className="editor-content-view">
      {!hasPersistedSource && legacyExcerpt && (
        <div className="content-panel-banner" role="status">
          Questa presentazione è stata creata senza contenuto salvato. Incollalo per poter
          generare nuove slide da esso. Non modifica le slide esistenti.
        </div>
      )}
      <textarea
        className="source-area"
        rows={18}
        value={value}
        placeholder="Incolla qui il contenuto sorgente di questa presentazione…"
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
      />
      {!hasPersistedSource && legacyExcerpt && (
        <details
          className="content-panel-excerpt"
          open={excerptOpen}
          onToggle={(e) => setExcerptOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary>Estratto disponibile</summary>
          <pre className="content-panel-excerpt-body">{legacyExcerpt}</pre>
        </details>
      )}
      {hasPersistedSource && !deckSourceContent(deck).trim() && (
        <p className="muted content-panel-hint">
          Il contenuto sorgente è vuoto — «+ Slide da contenuto» resterà disabilitato finché non
          incolli del testo.
        </p>
      )}
    </div>
  );
}
